import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtDate } from "@/lib/format";
import { ShieldCheck, KeyRound, LogOut, Lock, Smartphone, LifeBuoy } from "lucide-react";
import OwnerPinResetDialog from "@/components/OwnerPinResetDialog";
import QuickLoginSecurity from "@/components/QuickLoginSecurity";
import AutoLockSettings from "@/components/AutoLockSettings";
import { useAuth } from "@/context/AuthContext";

export default function Security() {
  const [sec, setSec] = useState(null);
  const [pinDlg, setPinDlg] = useState(null);
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const { user } = useAuth();
  const load = () => api.get("/auth/security").then((r) => setSec(r.data)).catch(() => setSec(null));
  useEffect(() => { setSec(null); load(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const isOwner = user?.role === "owner" && sec?.role === "owner";

  const change = async (e) => {
    e.preventDefault();
    if (np.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (np !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: np });
      if (data.token) localStorage.setItem("neec_token", data.token);
      toast.success("Password changed. Other sessions signed out");
      setCur(""); setNp(""); setConfirm(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const logoutOthers = async () => {
    try {
      const { data } = await api.post("/auth/logout-others");
      if (data.token) localStorage.setItem("neec_token", data.token);
      toast.success("All other sessions have been signed out");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><ShieldCheck className="w-7 h-7" /> Security</h1>
        <p className="text-muted-foreground">Manage your password, sessions and account security.</p>
      </div>

      <Card className="p-6">
        <p className="font-head font-bold flex items-center gap-2 mb-4"><KeyRound className="w-5 h-5" /> Change Password</p>
        <form onSubmit={change} className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label className="text-xs">Current Password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="sec-current" /></div>
          <div><Label className="text-xs">New Password</Label><Input type="password" value={np} onChange={(e) => setNp(e.target.value)} placeholder="At least 8 characters" data-testid="sec-new" /></div>
          <div><Label className="text-xs">Confirm New Password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="sec-confirm" /></div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sec-change-submit">{busy ? "Saving…" : "Update Password"}</Button>
            <p className="text-xs text-muted-foreground mt-2">Changing your password requires your current password and will sign out every other device.</p>
          </div>
        </form>
      </Card>

      <QuickLoginSecurity sec={sec} onChanged={load} />
      {isOwner && <AutoLockSettings />}

      {isOwner && (
        <Card className={`p-6 ${sec?.pin_recovery_pending ? "border-amber-500/60" : ""}`} data-testid="sec-owner-pin">
          <p className="font-head font-bold flex items-center gap-2 mb-2"><KeyRound className="w-5 h-5" /> Owner Authorization PIN</p>
          <p className="text-xs text-muted-foreground mb-4">Your 6-digit PIN that approves protected actions (refunds, over-limit discounts, Open Account, deletions, pickup corrections). It is never displayed and never logs anyone in. If you forget it, verify with your password{sec?.mfa_enabled ? " and authenticator" : ""} and set a new one.</p>
          {sec?.pin_recovery_pending && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 mb-3 text-sm flex items-start gap-2" data-testid="sec-pin-recovery-banner">
              <LifeBuoy className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div><p className="font-semibold text-amber-700 dark:text-amber-400">Recovery PIN issued by NEEC Support</p>
                <p className="text-xs text-muted-foreground">Your previous PIN has been cancelled. Enter the temporary PIN and create a new permanent PIN before {sec.pin_recovery_expires_at ? new Date(sec.pin_recovery_expires_at).toLocaleString() : "it expires"}.</p></div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm mr-2">Status: {sec?.has_pin ? <Badge className="bg-violet-500/10 text-violet-500 border-violet-500/20 text-[10px]">Set{sec?.pin_changed_at ? ` · changed ${fmtDate(sec.pin_changed_at)}` : ""}</Badge> : <Badge variant="outline" className="text-[10px]">Not set</Badge>}</span>
            {sec?.pin_recovery_pending
              ? <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setPinDlg("recover")} data-testid="sec-pin-recover-btn"><LifeBuoy className="w-4 h-4 mr-1.5" /> Complete PIN Recovery</Button>
              : <Button variant="outline" onClick={() => setPinDlg("reset")} data-testid="sec-pin-set-btn"><KeyRound className="w-4 h-4 mr-1.5" /> {sec?.has_pin ? "Change PIN" : "Set PIN"}</Button>}
            {sec?.has_pin && !sec?.pin_recovery_pending && <button type="button" className="text-xs text-violet-500 hover:underline" onClick={() => setPinDlg("reset")} data-testid="sec-forgot-pin-link">Forgot PIN?</button>}
          </div>
          <OwnerPinResetDialog open={!!pinDlg} mode={pinDlg || "reset"} onClose={() => setPinDlg(null)} onDone={load} />
        </Card>
      )}


      <Card className="p-6">
        <p className="font-head font-bold flex items-center gap-2 mb-4"><LogOut className="w-5 h-5" /> Active Sessions</p>
        <div className="flex items-center justify-between rounded-xl border border-border p-4">
          <div><p className="font-medium text-sm">Sign out other sessions</p><p className="text-xs text-muted-foreground">Keeps you signed in here, logs out everywhere else.</p></div>
          <Button variant="outline" onClick={logoutOthers} data-testid="sec-logout-others">Sign out others</Button>
        </div>
      </Card>

      <Card className="p-6">
        <p className="font-head font-bold flex items-center gap-2 mb-4"><Smartphone className="w-5 h-5" /> Multi-Factor Authentication</p>
        <div className="flex items-center justify-between rounded-xl border border-dashed border-border p-4 bg-secondary/30">
          <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-muted-foreground" /><div><p className="font-medium text-sm">Authenticator app (MFA)</p><p className="text-xs text-muted-foreground">Extra login protection.</p></div></div>
          <Badge variant="outline" className="text-[10px]">Coming Soon</Badge>
        </div>
      </Card>

      <Card className="p-6" data-testid="sec-overview">
        <p className="font-head font-bold mb-4">Account Security Overview</p>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between rounded-lg border border-border p-3"><span className="text-muted-foreground">Login email</span><span className="mono">{sec?.email}</span></div>
          <div className="flex justify-between rounded-lg border border-border p-3"><span className="text-muted-foreground">Role</span><span className="capitalize font-medium">{sec?.role}</span></div>
          {user?.role !== "cashier" && <div className="flex justify-between rounded-lg border border-border p-3"><span className="text-muted-foreground">Approval PIN</span><span>{sec?.has_pin ? <Badge className="bg-violet-500/10 text-violet-500 border-violet-500/20 text-[10px]">Set</Badge> : "Not set"}</span></div>}
          <div className="flex justify-between rounded-lg border border-border p-3"><span className="text-muted-foreground">Password last changed</span><span>{sec?.password_changed_at ? fmtDate(sec.password_changed_at) : "-"}</span></div>
        </div>
      </Card>
    </div>
  );
}
